import React, { useState, useEffect, useCallback } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import {
  PlusIcon,
  TrashIcon,
  ScissorsIcon,
  CheckCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface AllocationRule {
  client_id: string;
  client_name: string;
  percentage: number;
  billing_contact_email?: string;
}

interface BillAllocation {
  allocation_id: string;
  matter_id: string;
  name: string;
  allocation_rules: AllocationRule[];
  is_active: boolean;
}

interface Props {
  matterId: string;
  billId?: string;
  onSplitComplete?: () => void;
}

const AllocationEditor: React.FC<Props> = ({ matterId, billId, onSplitComplete }) => {
  const { keycloak } = useKeycloak();
  const [allocation, setAllocation] = useState<BillAllocation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [splitting, setSplitting] = useState(false);

  // Edit form
  const [editing, setEditing] = useState(false);
  const [formName, setFormName] = useState('');
  const [formRules, setFormRules] = useState<AllocationRule[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const getAuthHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${keycloak?.token || ''}`,
  }), [keycloak?.token]);

  const fetchAllocation = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/v1/billing/allocations/matter/${matterId}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) {
        setAllocation(null);
        return;
      }
      const body = await res.json();
      setAllocation(body.data || null);
    } catch {
      setAllocation(null);
    } finally {
      setLoading(false);
    }
  }, [matterId, getAuthHeaders]);

  useEffect(() => {
    fetchAllocation();
  }, [fetchAllocation]);

  const openForm = (existing?: BillAllocation) => {
    setEditing(true);
    if (existing) {
      setFormName(existing.name);
      setFormRules([...existing.allocation_rules]);
    } else {
      setFormName('');
      setFormRules([
        { client_id: '', client_name: '', percentage: 50 },
        { client_id: '', client_name: '', percentage: 50 },
      ]);
    }
  };

  const addParty = () => {
    setFormRules((prev) => [...prev, { client_id: '', client_name: '', percentage: 0 }]);
  };

  const removeParty = (idx: number) => {
    if (formRules.length <= 2) return;
    setFormRules((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateParty = (idx: number, field: keyof AllocationRule, value: any) => {
    setFormRules((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const percentageTotal = formRules.reduce((sum, r) => sum + (r.percentage || 0), 0);

  const handleSave = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        matter_id: matterId,
        name: formName,
        allocation_rules: formRules,
      };
      const url = allocation
        ? `/api/v1/billing/allocations/${allocation.allocation_id}`
        : '/api/v1/billing/allocations';
      const method = allocation ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.description || 'Save failed.');
      }
      setEditing(false);
      fetchAllocation();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSplit = async () => {
    if (!allocation || !billId) return;
    if (!confirm(`Split this bill across ${allocation.allocation_rules.length} parties? The original bill will be cancelled.`)) return;
    setSplitting(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/billing/allocations/split', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          source_bill_id: billId,
          allocation_id: allocation.allocation_id,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.description || 'Split failed.');
      }
      onSplitComplete?.();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSplitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4">
        <div className="h-4 w-4 border-2 border-lex-accent border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-gray-500 dark:text-gray-400">Loading allocation...</span>
      </div>
    );
  }

  // Edit form
  if (editing) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">
            {allocation ? 'Edit Split Allocation' : 'New Split Allocation'}
          </h3>
          <button onClick={() => setEditing(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Allocation Name</label>
          <input
            type="text"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder="e.g. Smith/Jones 60/40 Split"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
          />
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Parties</label>
            <button onClick={addParty} className="text-xs text-lex-accent hover:underline flex items-center gap-1">
              <PlusIcon className="h-3 w-3" /> Add Party
            </button>
          </div>

          {formRules.map((rule, idx) => (
            <div key={idx} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex-1 grid grid-cols-3 gap-2">
                <input
                  type="text"
                  value={rule.client_id}
                  onChange={(e) => updateParty(idx, 'client_id', e.target.value)}
                  placeholder="Client ID (UUID)"
                  className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-xs"
                />
                <input
                  type="text"
                  value={rule.client_name}
                  onChange={(e) => updateParty(idx, 'client_name', e.target.value)}
                  placeholder="Client Name"
                  className="px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-xs"
                />
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={rule.percentage || ''}
                    onChange={(e) => updateParty(idx, 'percentage', parseFloat(e.target.value) || 0)}
                    className="w-20 px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-xs"
                  />
                  <span className="text-xs text-gray-500">%</span>
                </div>
              </div>
              {formRules.length > 2 && (
                <button onClick={() => removeParty(idx)} className="text-red-400 hover:text-red-600">
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}

          <div className={`text-sm font-medium ${Math.abs(percentageTotal - 100) < 0.01 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            Total: {percentageTotal.toFixed(1)}%
            {Math.abs(percentageTotal - 100) >= 0.01 && ' (must equal 100%)'}
          </div>
        </div>

        <div className="flex items-center gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setEditing(false)}
            className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={submitting || !formName || Math.abs(percentageTotal - 100) >= 0.01}
            className="px-4 py-2 text-sm font-medium bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover disabled:opacity-50 transition-colors"
          >
            {submitting ? 'Saving...' : 'Save Allocation'}
          </button>
        </div>
      </div>
    );
  }

  // Display existing allocation
  if (allocation) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ScissorsIcon className="h-4 w-4 text-gray-400" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">Split Billing</h3>
          </div>
          <button
            onClick={() => openForm(allocation)}
            className="text-xs text-lex-accent hover:underline"
          >
            Edit
          </button>
        </div>

        {error && <p className="text-sm text-red-600 dark:text-red-400 mb-2">{error}</p>}

        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{allocation.name}</p>

        <div className="space-y-2">
          {allocation.allocation_rules.map((rule, idx) => (
            <div key={idx} className="flex items-center justify-between py-1.5 px-3 bg-gray-50 dark:bg-gray-700/50 rounded">
              <span className="text-sm text-gray-900 dark:text-white">{rule.client_name}</span>
              <div className="flex items-center gap-2">
                <div className="w-24 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                  <div
                    className="bg-lex-accent h-2 rounded-full"
                    style={{ width: `${rule.percentage}%` }}
                  />
                </div>
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300 w-10 text-right">
                  {rule.percentage}%
                </span>
              </div>
            </div>
          ))}
        </div>

        {billId && (
          <button
            onClick={handleSplit}
            disabled={splitting}
            className="mt-3 flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
          >
            <ScissorsIcon className="h-4 w-4" />
            {splitting ? 'Splitting...' : 'Split This Bill'}
          </button>
        )}
      </div>
    );
  }

  // No allocation — show setup button
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-4">
      <div className="flex items-center gap-2 mb-2">
        <ScissorsIcon className="h-4 w-4 text-gray-400" />
        <h3 className="text-sm font-medium text-gray-900 dark:text-white">Split Billing</h3>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        Configure split billing to divide invoices among multiple clients (e.g. co-counsel, insurance co-pay).
      </p>
      <button
        onClick={() => openForm()}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        <PlusIcon className="h-4 w-4" />
        Set Up Split Allocation
      </button>
    </div>
  );
};

export default AllocationEditor;
